/**
 * Browser-extension noise in the Sentry feed.
 *
 * Safari hides an injected script behind `webkit-masked-url://`, and Chrome,
 * Firefox and Safari's own extension runtimes use their schemes. A password
 * manager's RPC timing out is an unhandled rejection in the page, but every
 * frame belongs to the extension. Nothing in this app threw, and there is
 * nothing here to fix — the feed just cannot tell it from a real failure.
 *
 * The SDK's `denyUrls` is the usual switch for this and the wrong one. It
 * matches only the top frame, so an extension frame sitting above a frame of
 * ours would drop an error that also ran our code. It also skips an event
 * with no stack, which is what we want, but it cannot keep a mixed stack.
 *
 * Nor `in_app`: the browser SDK marks every frame it parses `in_app: true`,
 * extension scripts included, so that flag does not separate them.
 *
 * An event is dropped only when it has a stack and every frame of it is an
 * extension script. A frame of ours anywhere in the stack is kept, and so is
 * an event with no stack — a first-party rejection the browser did not
 * annotate must still be filed.
 */
import type { ErrorEvent, StackFrame } from '@sentry/react'

/**
 * Schemes the browsers use for extension scripts. Matched on the frame URL,
 * not the error message, so an app error that happens to mention one of these
 * words is still reported.
 *
 * The colon is the whole delimiter. Safari's stack lines, and the SDK's
 * rewrite of them (`safari-extension:` plus the script path), do not always
 * carry the slashes.
 */
const EXTENSION_FRAME_URL =
  /^(?:webkit-masked-url|safari-web-extension|safari-extension|chrome-extension|moz-extension):/i

/**
 * `beforeSend` hook. Returning `null` drops the event; otherwise it goes out
 * unchanged.
 */
export function dropBrowserExtensionNoise(event: ErrorEvent): ErrorEvent | null {
  return isBrowserExtensionNoise(event) ? null : event
}

/** True when the event has a stack and every frame of it is an extension script. */
function isBrowserExtensionNoise(event: ErrorEvent): boolean {
  const frames = stackFrames(event)
  return frames.length > 0 && frames.every(isExtensionFrame)
}

function stackFrames(event: ErrorEvent): StackFrame[] {
  const frames: StackFrame[] = []
  for (const exception of event.exception?.values ?? []) {
    appendFrames(frames, exception.stacktrace?.frames)
  }
  for (const thread of event.threads?.values ?? []) {
    appendFrames(frames, thread.stacktrace?.frames)
  }
  return frames
}

function appendFrames(frames: StackFrame[], more: StackFrame[] | undefined): void {
  if (more) frames.push(...more)
}

/**
 * A frame counts as the extension's only when every URL it carries is an
 * extension URL. A frame with no URL, or with one URL of ours beside one of
 * the extension's, is not — dropping on a guess would hide an app error.
 */
function isExtensionFrame(frame: StackFrame): boolean {
  const urls = frameUrls(frame)
  return urls.length > 0 && urls.every((url) => EXTENSION_FRAME_URL.test(url))
}

function frameUrls(frame: StackFrame): string[] {
  const urls: string[] = []
  if (frame.filename) urls.push(frame.filename)
  if (frame.abs_path && frame.abs_path !== frame.filename) urls.push(frame.abs_path)
  return urls
}
